'use client'

import React from 'react'

import * as RadixPopover from '@radix-ui/react-popover'
import { twMerge } from 'tailwind-merge'

import {
  PopoverProvider,
  useOpenPopoverId,
  usePopoverStore,
  useSetOpenPopoverId,
} from '@/components/common/PopoverV2/context'

import { useMounted } from '@/hooks/common/useMounted'

export interface PopoverProps extends RadixPopover.PopoverProps {
  children: React.ReactNode
}

export interface PopoverContentProps extends RadixPopover.PopoverContentProps {
  children?: React.ReactNode
  portal?: boolean
}

const PopoverV2: React.FC<PopoverProps> & {
  Trigger: typeof RadixPopover.Trigger
  Anchor: typeof RadixPopover.Anchor
  Close: typeof RadixPopover.Close
  Content: React.ForwardRefExoticComponent<
    PopoverContentProps & React.RefAttributes<HTMLDivElement>
  >
} = ({ children, ...props }) => {
  const isMounted = useMounted()

  if (!isMounted) {
    return null
  }

  return <RadixPopover.Root {...props}>{children}</RadixPopover.Root>
}

PopoverV2.Trigger = RadixPopover.Trigger
PopoverV2.Anchor = RadixPopover.Anchor
PopoverV2.Close = RadixPopover.Close
PopoverV2.Content = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  (
    {
      children,
      className,
      align = 'center',
      sideOffset = 4,
      portal = true,
      ...props
    },
    forwardedRef,
  ) => {
    // @/components/common/Command does not work when inside a popover portal
    const handleCloseAutoFocus = (e: Event) => {
      e.preventDefault()
    }
    const content = (
      <RadixPopover.Content
        ref={forwardedRef}
        align={align}
        sideOffset={sideOffset}
        className={twMerge(
          'z-50 inline-block w-72 rounded-md border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-900 p-4 shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className,
        )}
        onCloseAutoFocus={handleCloseAutoFocus}
        {...props}
      >
        {children}
      </RadixPopover.Content>
    )
    if (portal) {
      return <RadixPopover.Portal>{content}</RadixPopover.Portal>
    }
    return content
  },
)

PopoverV2.Content.displayName = 'PopoverV2.Content'

export {
  PopoverProvider,
  useOpenPopoverId,
  useSetOpenPopoverId,
  usePopoverStore,
}

export default PopoverV2
